import React from "react";
import { $, merge } from "glamor";
import TextField from "components/form/text-field";
import moment from "moment";
import { formatMessage } from "lib/globalize-util";
import { IShipDate } from "records/order";
import DeleteIcon from "@material-ui/icons/Delete";
import IconButton from "@material-ui/core/IconButton";
import { DATATEST } from "constants/integration";

interface IShipDatePickerProps {
    shipDate: IShipDate;
    index: number;
    onChange: (index, date) => void;
    onRemove: () => void;
    isDuplicate: boolean;
    disabled?: boolean;
}

export const ShipDatePicker = React.forwardRef<HTMLDivElement, IShipDatePickerProps>(
    (
        {
            // prettier ain't pretty
            shipDate,
            index,
            isDuplicate,
            disabled,
            onChange,
            onRemove,
        },
        divRef
    ) => {
        const [value, setValue] = React.useState(moment(shipDate.ship_start).format("YYYY-MM-DD"));

        const styles = React.useMemo(() => {
            return merge(
                {
                    display: "flex",
                    flexFlow: "column nowrap",
                    marginBottom: "18px",
                },
                $(" .shipDateRow", {
                    display: "flex",
                    marginBottom: "4px",
                }),
                $(" .itemsWarning", {
                    fontSize: "1rem",
                    fontStyle: "italic",
                }),
                $(" .deleteIcon", {
                    marginLeft: "16px",
                    fontSize: "2.2rem",
                    marginTop: "34px",
                    cursor: "pointer",
                })
            );
        }, []);

        const isValid = React.useMemo(() => moment(value, "YYYY-MM-DD", true).isValid(), [value]);

        const errorMsg = React.useMemo(
            () =>
                isValid
                    ? isDuplicate
                        ? formatMessage("This ship date already exists. Please select another date")
                        : undefined
                    : formatMessage("Invalid date value"),
            [isValid, isDuplicate]
        );

        const handleChangeDate = React.useCallback(
            (event) => {
                const newValue = event.target.value;
                setValue(newValue);
                onChange(index, newValue);
            },
            [index, onChange]
        );

        const handleBlur = React.useCallback(
            (event) => {
                let newValue = event.target.value;
                if (moment(newValue).valueOf() < moment.utc().valueOf()) {
                    newValue = moment.utc().format("YYYY-MM-DD");
                }
                setValue(newValue);
                onChange(index, newValue);
            },
            [index, onChange]
        );

        return (
            <div ref={divRef} {...styles} data-test={DATATEST.ORDERS.BUILD_ORDER.SHIP_DATE_PICKER}>
                <div className="shipDateRow">
                    <TextField
                        type="date"
                        name={`shipment-${index}`}
                        label={`${formatMessage("Shipment")} ${index + 1}`}
                        variant="outlined"
                        disabled={!!shipDate.line_items?.length || disabled}
                        errorText={errorMsg}
                        min={moment.utc().format("YYYY-MM-DD")}
                        value={value}
                        onBlur={handleBlur}
                        onChange={handleChangeDate}
                        dataTest={DATATEST.ORDERS.BUILD_ORDER.SHIP_DATE_PICKER_INPUT}
                    />
                    <IconButton
                        className="deleteIcon"
                        color="inherit"
                        disabled={disabled}
                        onClick={onRemove}
                        data-test={DATATEST.ORDERS.BUILD_ORDER.SHIP_DATE_PICKER_DELETE}
                    >
                        <DeleteIcon />
                    </IconButton>
                </div>
                {!!shipDate.line_items?.length && (
                    <div className="itemsWarning">
                        {formatMessage(
                            "This shipment includes products. Go to your cart and move products to modify the ship date"
                        )}
                    </div>
                )}
            </div>
        );
    }
);

ShipDatePicker.displayName = "ShipDatePicker";
